import { useState } from 'react'
import Head from 'next/head'
import type { NextPage } from 'next'
import { Layout } from '../components/Layout'
import { Counters, Stat } from '../components/Stats'
import { BasicModal } from '../components/Modals/BasicModal'
import { fetchAuctionResults } from '../utils/api'
import { Table } from '../components/Tables'
import { ExternalAuctionResult } from '../utils/types'
import { format, subDays } from 'date-fns'

const salesFormatter = (num, digits) => {
  const lookup = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'k' },
    { value: 1e6, symbol: 'M' }
  ]
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/
  const item = lookup
    .slice()
    .reverse()
    .find(function (item) {
      return num >= item.value
    })
  return item
    ? (num / item.value).toFixed(digits).replace(rx, '$1') + item.symbol
    : '0'
}

const Previous = ({
  auctions,
  stats
}: {
  auctions: ExternalAuctionResult[]
  stats: Stat[]
}) => {
  const [open, setOpen] = useState(false)
  const formattedDate = format(subDays(new Date(), 1), 'LLLL d')

  return (
    <div>
      <Head>
        <title>Previous Sales</title>
        <meta
          name="description"
          content="Historical online sales results aggregated by Breeding Market"
        />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <div className="text-3xl sm:text-4xl text-gray-900 font-semibold">
        2022 Online Sale Totals
      </div>
      <Counters stats={stats} />
      <div className="flex flex-col sm:flex-row">
        <div className="text-xs font-medium text-gray-500 p-2 mr-auto">
          Last updated on {formattedDate}
        </div>
        <button
          className="hidden sm:flex text-xs font-medium text-gray-500 p-2 underline"
          onClick={() => setOpen(true)}
        >
          Where does this data come from?
        </button>
      </div>
      <div className="text-3xl sm:text-4xl text-gray-900 font-semibold my-4">
        Recent Auctions
      </div>
      <Table data={auctions} />
      <BasicModal
        open={open}
        setOpen={setOpen}
        text="These statistics are aggregated from the top livestock auction sites and are updated daily. We try to only include lots and sales of live animals (not semen, fundraisers, or unrelated lots)."
        title=""
        buttonText="Got it"
      />
    </div>
  )
}

Previous.getLayout = function getLayout(page: NextPage) {
  return <Layout>{page}</Layout>
}

export async function getStaticProps() {
  const { data } = await fetchAuctionResults()

  const grossSales = data.reduce(
    (total, auction) => total + auction.attributes.totalSales,
    0
  )
  const totalLots = data.reduce(
    (total, auction) => total + auction.attributes.totalLots,
    0
  )

  const stats = [
    {
      name: 'Gross Sales',
      stat: '$' + salesFormatter(grossSales, 1)
    },
    { name: 'Total Auctions', stat: data.length },
    {
      name: 'Total Lots',
      stat: totalLots
    },
    {
      name: 'Average Lot Sale',
      stat: '$' + salesFormatter(grossSales / totalLots, 1)
    }
  ]

  // Pass data to the page via props
  return { props: { auctions: data, stats } }
}

export default Previous
